// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import {
    chain,
    PriceBreakValidation,
    ValidatePriceBreaks,
} from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/ValidatePriceBreaks";
import { getPriceBreaksForCurrentQuoteLine } from "@insite/client-framework/Store/Pages/RfqQuoteDetails/RfqQuoteDetailsSelectors";
import { BreakPriceRfqModel, QuoteLineModel } from "@insite/client-framework/Types/ApiModels";

export const XngValidatePriceBreaks: any = (props: any) => {
    const priceBreaks = getPriceBreaksForCurrentQuoteLine(props.getState());
    if (!priceBreaks) {
        return false;
    }

    const quoteLine = props.getState().pages.rfqQuoteDetails.quoteLineForCalculation!;
    const maxQty = priceBreaks[priceBreaks.length - 1].endQty;
    for (let i = 0; i < priceBreaks.length; i++) {
        if (!shouldValidatePriceBreak(props.isJobQuote, quoteLine, priceBreaks[i])) {
            continue;
        }

        const validation: PriceBreakValidation = {
            index: i,
            priceRequired: false,
            invalidPrice: false,
            invalidQty: false,
        };
        validatePrice(validation, quoteLine, priceBreaks[i]);
        if (!props.isJobQuote) {
            const isFixedQty = props.parameter.isFixedQty;
            const minQty = parseInt(props.parameter.minQty, 10) || 0;
            validateStartQty(validation, priceBreaks, maxQty, minQty, isFixedQty);
            validateEndQty(validation, priceBreaks, maxQty);
        }

        props.result.validations.push(validation);
    }
};

function shouldValidatePriceBreak(
    isJobQuote: boolean,
    quoteLine: QuoteLineModel,
    priceBreak: BreakPriceRfqModel,
): boolean {
    if (!isJobQuote) {
        return true;
    }

    return (
        quoteLine!
            .pricingRfq!.priceBreaks!.slice()
            .sort((a, b) => b.startQty - a.startQty)
            .filter(x => x.startQty <= quoteLine!.qtyOrdered!)[0].startQty === priceBreak.startQty
    );
}

function validatePrice(result: PriceBreakValidation, quoteLine: QuoteLineModel, priceBreak: BreakPriceRfqModel) {
    if (priceBreak.price === undefined || priceBreak.price.toString() === "" || Number.isNaN(priceBreak.price)) {
        result.priceRequired = true;
    }
    if (
        (quoteLine!.pricingRfq!.minimumPriceAllowed > 0 &&
            priceBreak.price < quoteLine!.pricingRfq!.minimumPriceAllowed) ||
        priceBreak.price < 0
    ) {
        result.invalidPrice = true;
    }
}

function validateStartQty(
    result: PriceBreakValidation,
    priceBreaks: BreakPriceRfqModel[],
    maxQty: number,
    minQty: number,
    isFixedQty: boolean,
) {
    const { index } = result;

    if (index === 0) {
        if (!isFixedQty && minQty > 0 && priceBreaks[0].startQty < minQty) {
            result.invalidQty = true;
        }

        if (priceBreaks[0].startQty !== 1 && !isFixedQty && minQty < 1) {
            result.invalidQty = true;
        }
    } else {
        if (
            priceBreaks[index].startQty <= priceBreaks[index - 1].startQty ||
            priceBreaks[index].startQty < priceBreaks[index - 1].endQty
        ) {
            result.invalidQty = true;
        }

        if (
            index === priceBreaks.length - 1 &&
            priceBreaks[priceBreaks.length - 1].startQty &&
            maxQty > 0 &&
            maxQty < priceBreaks[priceBreaks.length - 1].startQty
        ) {
            result.invalidQty = true;
        }
    }
}

function validateEndQty(result: PriceBreakValidation, priceBreaks: BreakPriceRfqModel[], maxQty: number) {
    const { index } = result;
    if (index === priceBreaks.length - 1 && maxQty > 0 && maxQty < priceBreaks[priceBreaks.length - 1].startQty) {
        result.invalidQty = true;
    }
}

replaceInChain(chain, ValidatePriceBreaks, XngValidatePriceBreaks);
